import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, Target, Zap, Sparkles, CheckCircle2 } from 'lucide-react';

export const Services = () => {
  const services = [
    {
      icon: Target,
      title: 'GEO / AIO Optimization',
      description: 'Get Your Brand Referenced in AI Answers',
      longDescription: 'Generative Engine Optimization (GEO) and AI Visibility Optimization (AIO) ensure that when people ask AI tools for solutions, your brand is the one they recommend.',
      image: 'https://images.unsplash.com/photo-1745674684539-d90293d659a9?w=800&h=600&fit=crop',
      features: [
        'Content & structure optimization',
        'Schema and structured data implementation',
        'Authority building campaigns',
        'AI visibility tracking and reporting',
        'Competitor citation analysis'
      ],
      link: '/services/geo-aio'
    },
    {
      icon: Zap,
      title: 'AI Ads',
      description: 'Reach People Inside ChatGPT and Other AI Experiences',
      longDescription: 'Prepare for the new wave of AI-native ad inventory—from ChatGPT and beyond—with campaigns built for trust, relevance, and performance.',
      image: 'https://images.unsplash.com/photo-1745674684468-b9fc392fda3f?w=800&h=600&fit=crop',
      features: [
        'AI ad platform strategy',
        'Conversational ad creative',
        'Campaign setup and management',
        'Performance tracking and optimization',
        'Integration with GEO/AIO efforts'
      ],
      link: '/services/ai-ads'
    },
    {
      icon: Sparkles,
      title: 'AI Agents & Automations',
      description: 'Programmatic GEO/GEO automations that convert traffic',
      longDescription:
        'We build AI agents and automations for content and programmatic SEO pages around trending GEO-aligned keywords, plus the follow-up workflows that turn that visibility into pipeline.',
      image: 'https://images.unsplash.com/photo-1761305135319-70b8dbde3525?w=800&h=600&fit=crop',
      features: [
        'Lead capture AI agents',
        'Appointment booking automation',
        'Follow-up and nurturing sequences',
        'CRM and tool integrations',
        'Internal operations automation'
      ],
      link: '/services/ai-agents'
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Services That Make You{' '}
            <span className="text-gradient">Visible in AI Search</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            From optimizing your content for AI engines to running ads inside ChatGPT, we offer a complete suite of services to dominate AI-driven discovery.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div className="w-12 h-12 rounded-lg gradient-primary flex items-center justify-center mb-6">
                    <service.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
                    {service.title}
                  </h2>
                  <p className="text-xl text-muted-foreground mb-6">
                    {service.description}
                  </p>
                  <p className="text-foreground mb-6">
                    {service.longDescription}
                  </p>
                  
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link to={service.link}>
                    <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
                      Learn More
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
                
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <Card className="overflow-hidden border-border hover:shadow-xl transition-shadow">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover"
                    />
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get a free GEO/AIO audit to see where your brand stands in AI search and what opportunities you're missing.
          </p>
          <Link to="/free-audit">
            <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
              Get My Free Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
